import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../api-response.model';

export interface XeroApplicationSummary {
  providerApplicationId: number;
  applicationCode: string;
  environmentCode: string;
  clientId: string;
  redirectUri: string;
  scopes: string;
  hasClientSecret: boolean;
  isDefault: boolean;
  isActive: boolean;
  clientSecretLastRotatedAtUtc: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface XeroCurrentApplication {
  configured: boolean;
  applicationCode?: string;
  environmentCode?: string;
  clientId?: string;
  hasClientSecret?: boolean;
  isDefault?: boolean;
  isActive?: boolean;
}

export interface XeroCredentialsUpdateRequest {
  environmentCode: string;
  clientId: string;
  clientSecret: string;
}

export interface XeroConnectUrlResponse {
  authorizationUrl: string;
  expiresInMinutes: number;
  redirectUri: string;
  returnUrl?: string | null;
  compId: number;
  applicationCode: string;
}

export interface XeroSyncJobSummary {
  jobId: string;
  connectionId: string;
  entityCode: string;
  syncMode: string;
}

export interface XeroSyncTriggerResponse {
  accepted: boolean;
  jobCount: number;
  jobs: XeroSyncJobSummary[];
  entities: string[];
  requestedAtUtc: string;
  statusEndpoint: string;
  combinedStatusEndpoint: string;
  errorsEndpoint: string;
}

export interface XeroSyncTimerStatus {
  kind: string;
  enabled: boolean;
  intervalMinutes: number;
  nextRunAtUtc: string | null;
  secondsUntilNextRun: number | null;
  lastTriggeredAtUtc: string | null;
  lastCompletedAtUtc: string | null;
  lastError: string | null;
}

export interface XeroSyncStatusResponse {
  compId: number;
  serverTimeUtc: string;
  tenantSchedule: unknown;
  timer: XeroSyncTimerStatus;
  counts: unknown;
  diagnostics: unknown;
  latestJobs: unknown[];
  recentErrors: unknown[];
}

export interface XeroSyncRequestItem {
  apiRequestId: number;
  jobId: string;
  batchId: string | null;
  connectionId: string;
  httpMethod: string;
  endpoint: string;
  startedAtUtc: string;
  completedAtUtc: string | null;
  durationMs: number | null;
  throttleWaitDurationMs: number | null;
  httpStatusCode: number | null;
  retryAfterSeconds: number | null;
  minuteLimitRemaining: number | null;
  appMinuteLimitRemaining: number | null;
  dailyLimitRemaining: number | null;
  errorCode: string | null;
  errorMessage: string | null;
}

export interface XeroSyncRequestsResponse {
  count: number;
  telemetrySchema: unknown;
  items: XeroSyncRequestItem[];
}

export interface XeroSyncDiagnosticsResponse {
  windowHours: number;
  telemetrySchema: unknown;
  totals: unknown;
  latest429: unknown[];
  duplicateActiveJobs: unknown[];
  suspiciousDuplicateCalls: unknown[];
  retryGroups: unknown[];
  zeroTableExplanation: unknown[];
}

/**
 * The `environmentCode` Xero credentials are stored under has nothing to do with which
 * Xero org is connected — it's which tier of *our* app saved them, so the backend can
 * keep local/QA/production credentials for the same tenant separate. Never user-facing;
 * derived from the running Angular environment, matching the backend's own
 * IHostEnvironment.EnvironmentName values (Development / Staging / Production).
 */
export function resolveXeroEnvironmentCode(): string {
  switch (environment.name) {
    case 'production':
      return 'Production';
    case 'qa':
      return 'Staging';
    default:
      return 'Development';
  }
}

@Injectable({ providedIn: 'root' })
export class XeroIntegrationService {
  private readonly http = inject(HttpClient);
  private readonly apiBase = environment.apiBaseUrl;
  private readonly base = `${this.apiBase}/integrations/xero`;

  getTrialBalance(date: string): Observable<unknown> {
    const params = new HttpParams().set('date', date);
    return this.http.get<unknown>(`${this.base}/reports/trial-balance`, { params });
  }

  syncTrialBalance(date: string): Observable<unknown> {
    const params = new HttpParams().set('date', date);
    return this.http.post<unknown>(`${this.base}/reports/trial-balance/sync`, {}, { params });
  }

  getApplications(): Observable<ApiResponse<XeroApplicationSummary[]>> {
    return this.http.get<ApiResponse<XeroApplicationSummary[]>>(`${this.base}/configuration/applications`);
  }

  getCurrentApplication(): Observable<ApiResponse<XeroCurrentApplication>> {
    return this.http.get<ApiResponse<XeroCurrentApplication>>(`${this.base}/configuration/applications/current`);
  }

  updateCredentials(applicationCode: string, payload: { clientId: string; clientSecret: string }): Observable<ApiResponse<unknown>> {
    const request: XeroCredentialsUpdateRequest = {
      environmentCode: resolveXeroEnvironmentCode(),
      clientId: payload.clientId,
      clientSecret: payload.clientSecret,
    };
    return this.http.put<ApiResponse<unknown>>(
      `${this.base}/configuration/applications/${encodeURIComponent(applicationCode)}/credentials`,
      request
    );
  }

  getConnectUrl(applicationCode?: string, returnUrl?: string): Observable<ApiResponse<XeroConnectUrlResponse>> {
    let params = new HttpParams();
    if (applicationCode) params = params.set('applicationCode', applicationCode);
    if (returnUrl) params = params.set('returnUrl', returnUrl);
    return this.http.get<ApiResponse<XeroConnectUrlResponse>>(`${this.base}/oauth/connect-url`, { params });
  }

  triggerSyncAll(): Observable<ApiResponse<XeroSyncTriggerResponse>> {
    return this.http.post<ApiResponse<XeroSyncTriggerResponse>>(`${this.base}/sync/all`, {});
  }

  getSyncStatus(take = 50): Observable<ApiResponse<XeroSyncStatusResponse>> {
    const params = new HttpParams().set('take', take);
    return this.http.get<ApiResponse<XeroSyncStatusResponse>>(`${this.base}/sync/status`, { params });
  }

  getSyncRequests(take = 200, errorsOnly = false): Observable<ApiResponse<XeroSyncRequestsResponse>> {
    const params = new HttpParams().set('take', take).set('errorsOnly', errorsOnly);
    return this.http.get<ApiResponse<XeroSyncRequestsResponse>>(`${this.base}/sync/requests`, { params });
  }

  getSyncDiagnostics(): Observable<ApiResponse<XeroSyncDiagnosticsResponse>> {
    return this.http.get<ApiResponse<XeroSyncDiagnosticsResponse>>(`${this.base}/sync/diagnostics`);
  }
}
